import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronUp,
  FolderClosed,
  FolderKanban,
  Monitor,
  MoreHorizontal,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  SquarePen,
  Star,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
  type KeyboardEvent,
  type MouseEvent,
  type TouchEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useFocusTrap, useInert } from '../hooks/useFocusTrap';
import { useSwipeToClose } from '../hooks/useSwipeToClose';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { type SessionRow, useDeleteSession, usePatchSession, useSessions } from '../lib/sessions';
import { SessionRowMenu } from './SessionRowMenu';
import { Wordmark } from './Wordmark';

export const SIDEBAR_ID = 'mc-sidebar';

type SidebarProps = {
  /** Drawer open state — only used below the md breakpoint. On
   * desktop the sidebar is always visible regardless of this. */
  open?: boolean;
  onClose?: () => void;
  /** Closed mobile drawer: drop the rail from AT and the tab order. */
  inactive?: boolean;
  /** Open mobile drawer: cycle Tab inside the rail. */
  trapFocus?: boolean;
};

/**
 * Persistent navigation on desktop; the same rail becomes a mobile drawer.
 */
function noop(): void {}

const DAY = 86_400_000;

/** Starred first, then by recency like native chat apps. */
function groupSessions(sessions: SessionRow[]): { label: string; items: SessionRow[] }[] {
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const buckets: [string, SessionRow[]][] = [
    ['Избранное', []],
    ['Сегодня', []],
    ['Вчера', []],
    ['Последние 7 дней', []],
    ['Последние 30 дней', []],
    ['Ранее', []],
  ];
  for (const s of sessions) {
    const raw = s.last_msg_at || s.created_at;
    const t = raw < 1e12 ? raw * 1000 : raw; // server stores unix seconds
    const i =
      s.starred > 0
        ? 0
        : t >= startOfToday
          ? 1
          : t >= startOfToday - DAY
            ? 2
            : t >= startOfToday - 7 * DAY
              ? 3
              : t >= startOfToday - 30 * DAY
                ? 4
                : 5;
    buckets[i][1].push(s);
  }
  return buckets
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

export function Sidebar({
  open = false,
  onClose,
  inactive = false,
  trapFocus = false,
}: SidebarProps = {}) {
  const { user } = useAuth();
  const { data: sessions } = useSessions();
  const remove = useDeleteSession();
  const navigate = useNavigate();
  const activeSessionId = useParams({ strict: false }).sessionId as string | undefined;
  const asideRef = useRef<HTMLElement>(null);
  useInert(asideRef, inactive);
  useFocusTrap(asideRef, trapFocus);
  useSwipeToClose(asideRef, trapFocus && !!onClose, onClose ?? noop);

  const groups = groupSessions(sessions ?? []);
  const deleteSession = (id: string): void => {
    void remove
      .mutateAsync(id)
      .then(() => {
        if (window.location.pathname === `/chat/${id}`) void navigate({ to: '/new' });
      })
      .catch(() => {
        // The mutation rolls back its optimistic row on failure.
      });
  };

  return (
    <aside
      ref={asideRef}
      id={SIDEBAR_ID}
      aria-label="Навигация"
      aria-hidden={inactive || undefined}
      aria-modal={trapFocus || undefined}
      role={trapFocus ? 'dialog' : undefined}
      className={cn(
        'mc-safe-top mc-safe-x mc-chrome flex flex-col bg-sidebar',
        // Desktop: static rail.
        'md:static md:w-[272px] md:shrink-0 md:translate-x-0 md:pointer-events-auto md:shadow-none md:border-r md:border-border',
        // Mobile: fixed drawer that slides in from the left.
        'mc-drawer-motion fixed inset-y-0 left-0 z-40 w-[86vw] max-w-[340px] transform',
        open ? 'translate-x-0' : '-translate-x-full pointer-events-none',
      )}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2 md:pt-4">
        <button
          type="button"
          onClick={onClose}
          className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-2 focus-visible:right-16 focus-visible:z-10 focus-visible:rounded-full focus-visible:bg-surface focus-visible:px-3 focus-visible:py-2 focus-visible:text-[14px] md:hidden"
        >
          Закрыть меню
        </button>
        <Wordmark className="text-[23px]" />
        <Link
          to="/new"
          className="mc-press inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground active:bg-subtle md:hover:bg-subtle"
          title="Новый чат"
          aria-label="Новый чат"
        >
          <SquarePen size={19} strokeWidth={1.8} />
        </Link>
      </div>

      <div className="px-3 pb-2">
        <Link
          to="/search"
          className="mc-press flex h-11 w-full items-center gap-2.5 rounded-2xl bg-subtle px-3.5 text-[15px] text-muted-foreground"
        >
          <Search size={17} strokeWidth={1.8} />
          Поиск
        </Link>
      </div>

      <nav className="space-y-0.5 px-2 pb-1" aria-label="Разделы">
        <NavItem
          to="/projects"
          icon={<FolderKanban size={19} strokeWidth={1.7} />}
          label="Проекты"
        />
        <NavItem to="/files" icon={<FolderClosed size={19} strokeWidth={1.7} />} label="Файлы" />
        <NavItem
          to="/settings"
          icon={<SettingsIcon size={19} strokeWidth={1.7} />}
          label="Настройки"
        />
        {user?.mac_screen_available && (
          <NavItem to="/mac" icon={<Monitor size={19} strokeWidth={1.7} />} label="Экран Мака" />
        )}
        {user?.is_admin && (
          <NavItem
            to="/admin"
            icon={<ShieldCheck size={19} strokeWidth={1.7} />}
            label="Администрирование"
          />
        )}
      </nav>

      <div className="mt-1 flex-1 overflow-y-auto overscroll-contain px-2 pb-3">
        {groups.length === 0 ? (
          <div className="px-3 py-6 text-[14px] text-muted-foreground">Пока нет чатов</div>
        ) : (
          groups.map((group) => (
            <section key={group.label} aria-label={group.label} className="mt-3 first:mt-1">
              <h3 className="px-3 pb-1 text-[13px] font-medium text-muted-foreground">
                {group.label}
              </h3>
              <AnimatePresence initial={false} mode="popLayout">
                {group.items.map((s) => (
                  <motion.div
                    key={s.id}
                    layout="position"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ type: 'spring', stiffness: 520, damping: 42 }}
                  >
                    <RecentRow
                      session={s}
                      active={s.id === activeSessionId}
                      onDeleteSession={deleteSession}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </section>
          ))
        )}
      </div>

      <UserChip username={user?.username} />
    </aside>
  );
}

function UserChip({ username }: { username: string | undefined }) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const avatarLetter = (username ?? '?').charAt(0).toUpperCase();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: globalThis.MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative mx-2 mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-xl p-2 text-left hover:bg-subtle"
        title="Аккаунт"
      >
        <div className="relative shrink-0">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-primary-foreground"
            style={{ background: 'var(--color-accent)' }}
          >
            {avatarLetter}
          </div>
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-medium">{username}</div>
          <div className="text-[12px] text-muted-foreground">Рабочее пространство</div>
        </div>
        {open ? (
          <ChevronDown size={12} className="text-muted-foreground" />
        ) : (
          <ChevronUp size={12} className="text-muted-foreground" />
        )}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Аккаунт"
          className="mc-menu-enter origin-bottom absolute bottom-full left-0 right-0 z-30 mb-1 rounded-2xl border border-border/70 bg-surface p-1.5 text-[14px] mc-float"
        >
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center rounded-md px-3 py-2 text-left hover:bg-subtle"
          >
            Настройки
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setLogoutError(null);
              void logout()
                .then(() => setOpen(false))
                .catch((err: unknown) => {
                  setLogoutError(err instanceof Error ? err.message : 'Не удалось выйти.');
                  setOpen(true);
                });
            }}
            className="flex w-full items-center rounded-md px-3 py-2 text-left text-destructive hover:bg-subtle"
          >
            Выйти
          </button>
          {logoutError && (
            <div role="alert" className="px-3 py-2 text-[12px] text-destructive">
              {logoutError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Single Recents row. Renders a Link to the chat plus a `...`
 * button that opens the per-row menu. Doubles as the inline
 * rename surface — when the user picks Rename, the label swaps
 * into a <input> and Enter / blur commits.
 */
function RecentRow({
  session,
  active,
  onDeleteSession,
}: {
  session: SessionRow;
  active: boolean;
  onDeleteSession: (id: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(session.title);
  const patch = usePatchSession();
  const inputRef = useRef<HTMLInputElement>(null);
  const kebabRef = useRef<HTMLButtonElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [anchor, setAnchor] = useState<'kebab' | 'row'>('kebab');
  const starred = session.starred > 0;
  const openAtRow = (): void => {
    setAnchor('row');
    setMenuOpen(true);
  };
  const longPress = useLongPress(openAtRow);

  useEffect(() => {
    if (!renaming) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [renaming]);

  // Drop a stale draft if the server pushes a newer title
  // (e.g. the auto-rename from the first user message lands).
  useEffect(() => {
    if (!renaming) setDraft(session.title);
  }, [session.title, renaming]);

  function commitRename(): void {
    const next = draft.trim();
    setRenaming(false);
    if (!next || next === session.title) {
      setDraft(session.title);
      return;
    }
    patch.mutate({ id: session.id, patch: { title: next } });
  }

  function onInputKey(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(session.title);
      setRenaming(false);
    }
  }

  function onMenuClick(e: MouseEvent): void {
    // Prevent the Link wrapping the row from catching the click.
    e.preventDefault();
    e.stopPropagation();
    setAnchor('kebab');
    setMenuOpen((v) => !v);
  }

  function handleDelete(): void {
    // Confirmation is rendered inline by SessionRowMenu; by the time
    // this runs the user has already clicked through the confirm
    // button inside the popover. window.confirm() was flaky inside
    // iOS PWA standalone and blocked by some extensions on desktop.
    onDeleteSession(session.id);
  }

  if (renaming) {
    return (
      <div className={cn('rounded-lg px-2.5 py-1.5', active ? 'bg-bubble' : 'hover:bg-subtle')}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={onInputKey}
          className="w-full bg-transparent text-sm outline-none"
          aria-label="Переименовать чат"
        />
      </div>
    );
  }

  return (
    <div className="group relative">
      <Link
        ref={linkRef}
        to="/chat/$sessionId"
        params={{ sessionId: session.id }}
        {...longPress}
        className={cn(
          'flex min-h-11 w-full items-center gap-1.5 rounded-xl py-2 pr-3 pl-3 text-left text-[15px] text-foreground md:min-h-9 md:pr-8 md:text-[14px]',
          active ? 'bg-bubble' : 'hover:bg-subtle',
        )}
        title={session.title}
      >
        {starred && (
          <Star
            size={11}
            className="shrink-0 text-warning"
            fill="currentColor"
            aria-label="В избранном"
          />
        )}
        <span className="min-w-0 flex-1 truncate">{session.title}</span>
      </Link>
      <button
        ref={kebabRef}
        type="button"
        onClick={onMenuClick}
        aria-label="Действия с чатом"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={cn(
          'absolute top-1/2 right-1 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition hover:bg-subtle [@media(hover:hover)]:inline-flex',
          // Mouse devices reveal it on hover; touch opens the same menu
          // with a long press on the row, like native lists.
          menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
          menuOpen && 'bg-subtle',
        )}
      >
        <MoreHorizontal size={14} />
      </button>
      {menuOpen && (
        <SessionRowMenu
          session={session}
          onStar={(next) => patch.mutate({ id: session.id, patch: { starred: next } })}
          onRename={() => setRenaming(true)}
          onSetProject={(projectId) =>
            patch.mutate({ id: session.id, patch: { project_id: projectId } })
          }
          onDelete={handleDelete}
          onClose={() => setMenuOpen(false)}
          anchorRef={anchor === 'row' ? linkRef : kebabRef}
        />
      )}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  const base =
    'mc-press flex min-h-11 items-center gap-3 rounded-xl px-3 py-1.5 text-[15px] text-foreground whitespace-nowrap md:min-h-10 md:text-[14px]';

  return (
    <Link
      to={to}
      className={base}
      activeProps={{ className: 'bg-bubble font-medium' }}
      inactiveProps={{ className: 'hover:bg-subtle' }}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

/**
 * Touch long-press (~450 ms) that opens a context menu instead of
 * following the link. Movement cancels it so scrolling stays smooth.
 */
function useLongPress(onLongPress: () => void) {
  // Android/desktop deliver `contextmenu` for long press / right click:
  // open our menu there too instead of the browser's link menu.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const clear = (): void => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return {
    onTouchStart: (e: TouchEvent<HTMLElement>) => {
      const t = e.touches[0];
      start.current = { x: t.clientX, y: t.clientY };
      fired.current = false;
      clear();
      timer.current = setTimeout(() => {
        fired.current = true;
        navigator.vibrate?.(8);
        onLongPress();
      }, 450);
    },
    onTouchMove: (e: TouchEvent<HTMLElement>) => {
      const t = e.touches[0];
      const s0 = start.current;
      if (s0 && Math.hypot(t.clientX - s0.x, t.clientY - s0.y) > 10) clear();
    },
    onTouchEnd: clear,
    onTouchCancel: clear,
    onContextMenu: (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      clear();
      onLongPress(); // idempotent if the touch timer already opened it
    },
    onClick: (e: MouseEvent<HTMLElement>) => {
      if (!fired.current) return;
      e.preventDefault(); // the press opened the menu; don't navigate
      fired.current = false;
    },
  };
}
